import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import WhyChooseUsSection from '@/components/shared/WhyChooseUsSection';
import { CheckCircle2, ShieldCheck, Zap, Users, Briefcase, Award, TrendingUp, Heart, Settings, Layout, MessageSquare, Droplet, Boxes } from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Hero Section */}
        <section className="relative pt-20 pb-24 px-6 lg:px-12 overflow-hidden bg-gradient-to-b from-[#FFFDF9] to-[#F9F5EC]">
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="w-full lg:w-1/2 text-left z-10">
                <h3 className="text-[#8C6208] font-bold tracking-widest text-sm mb-4 uppercase">Tentang Kami</h3>
                <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.15] text-[#302621]">
                  Latar Belakang <br />Founder & CEO
                </h1>
                <div className="text-sm md:text-base text-[#7A695B] mb-10 leading-relaxed max-w-xl font-light space-y-4">
                  <p>Berawal dari latar belakang sebagai mahasiswa, beliau justru menemukan ketertarikan yang kuat terhadap dunia pemasaran dan pengembangan bisnis. Selama lebih dari sepuluh tahun, beliau mendalami berbagai strategi marketing, membangun jaringan, memahami perilaku konsumen, serta mempelajari bagaimana sebuah produk dapat diterima dan berkembang di pasar. Sebelum mendirikan pabrik, beliau terlebih dahulu membangun dan mengembangkan brand miliknya sendiri, sehingga memahami secara langsung berbagai tantangan yang dihadapi para pelaku usaha.</p>
                  
                  <p>Berbekal pengalaman sebagai pemilik brand, beliau kemudian mendirikan <strong className="text-[#8C6208]">Satala Dermatech Essential</strong>, sebuah perusahaan manufaktur dan pabrik maklon kosmetik yang berlokasi di Bogor, dengan konsentrasi utama pada pengembangan dan produksi parfum. Satala hadir untuk membantu para pemilik usaha dalam mengembangkan produk mulai dari tahap awal hingga siap dipasarkan.</p>
                  
                  <p>Sebagai pabrik maklon kosmetik dan parfum di Bogor, Satala Dermatech Essential menyediakan layanan terintegrasi, mulai dari konsultasi dan pengembangan konsep produk, pemilihan aroma dan bahan, formulasi, desain kemasan, proses produksi, hingga produk siap dipasarkan. Pengalamannya membuat beliau memahami bahwa setiap klien tidak hanya membutuhkan produsen, tetapi juga mitra yang mampu memahami visi, karakter, dan kebutuhan bisnis mereka.</p>
                  
                  <p>Di bawah kepemimpinannya, Satala Dermatech Essential terus bertumbuh dan telah dipercaya oleh lebih dari <strong className="text-[#8C6208]">150 brand</strong> untuk membantu mewujudkan berbagai produk kosmetik dan parfum. Dikenal sebagai sosok pemimpin yang berwibawa, komunikatif, humoris, dan dapat dipercaya, beliau menjadikan Satala bukan sekadar pabrik maklon, tetapi mitra bisnis yang hadir untuk mendampingi setiap brand agar dapat tumbuh, berkembang, dan bersaing di pasar.</p>
                  
                  <p className="font-bold text-[#8C6208] pt-2">— Umar Syarif, CEO</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1">
                    Lihat Katalog
                  </Button>
                  <Button size="lg" variant="outline" className="border-[#302621] text-[#302621] hover:bg-[#302621] hover:text-white px-8 py-6 text-base rounded-lg transition-transform hover:-translate-y-1">
                    Hubungi Kami
                  </Button>
                </div>
              </div>
              <div className="w-full lg:w-1/2 relative z-10 flex justify-center lg:justify-end">
                {/* CEO Portrait Graphic */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white border border-[#E6D5B8]/30 p-2 transform rotate-1 hover:rotate-0 transition-transform duration-500 w-[400px] h-auto mx-auto lg:mx-0">
                  <img src="/Owner Satala.png" alt="Umar Syarif, CEO" className="w-full h-auto rounded-xl object-cover aspect-[9/16]" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Siapa Kami Section */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="w-full lg:w-5/12">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img src="/Gambar Web/Pabrik Satala.png" alt="Laboratory Team" className="w-full h-[500px] object-cover" />
                </div>
              </div>
              <div className="w-full lg:w-7/12">
                <h3 className="text-[#8C6208] font-bold tracking-widest text-xs mb-3 uppercase">Siapa Kami</h3>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#302621] mb-6 leading-tight">
                  Kami Hadir Untuk Menyederhanakan Kompleksitas Bisnis Kosmetik
                </h2>
                <p className="text-[#7A695B] mb-10 leading-relaxed">
                  Satala Dermatech dikembangkan oleh tim profesional yang memiliki pengalaman luas dalam riset, formulasi, dan pengembangan produk kecantikan untuk berbagai skala bisnis di Indonesia.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-[#F9F5EC] flex items-center justify-center text-[#8C6208]">
                      <Briefcase className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621] mb-2 text-lg">Berpengalaman</h4>
                      <p className="text-sm text-[#7A695B] leading-relaxed">Bertahun-tahun dalam pengembangan industri kosmetik.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-[#F9F5EC] flex items-center justify-center text-[#8C6208]">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621] mb-2 text-lg">Fokus pada Klien</h4>
                      <p className="text-sm text-[#7A695B] leading-relaxed">Solusi yang disesuaikan dengan kebutuhan brand Anda.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-[#F9F5EC] flex items-center justify-center text-[#8C6208]">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#302621] mb-2 text-lg">Berkembang Bersama</h4>
                      <p className="text-sm text-[#7A695B] leading-relaxed">Terus berinovasi mengikuti perkembangan tren terkini.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Row */}
        <section className="py-12 bg-[#F9F5EC] border-y border-[#E6D5B8]/40" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { count: "150+", label: "Brand Kosmetik", icon: <Award className="w-8 h-8 text-[#D4AF37] mb-3" /> },
                { count: "500+", label: "Variant Unggulan", icon: <Boxes className="w-8 h-8 text-[#D4AF37] mb-3" /> },
                { count: "100rb+", label: "Produk Terjual", icon: <Settings className="w-8 h-8 text-[#D4AF37] mb-3" /> },
                { count: "99%", label: "Legalitas Terjamin", icon: <CheckCircle2 className="w-8 h-8 text-[#D4AF37] mb-3" /> }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm border border-[#E6D5B8]/20 flex flex-col items-center text-center">
                  {stat.icon}
                  <h3 className="text-3xl font-black text-[#302621] mb-1">{stat.count}</h3>
                  <p className="text-xs text-[#7A695B] font-medium uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Visi Misi */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Visi */}
              <div className="bg-[#FCFAF6] border border-[#E6D5B8]/30 p-10 rounded-3xl flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#EFE8D8] flex items-center justify-center text-[#8C6208] mb-6 shadow-sm">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="font-bold tracking-widest text-sm text-[#8C6208] uppercase mb-4">Visi Kami</h4>
                <p className="text-[#302621] text-base font-medium leading-relaxed">
                  Kami berkomitmen menjadi perusahaan pengembang dan penyedia solusi kosmetik terpercaya di Indonesia, yang berfokus pada inovasi, keamanan, dan kualitas berstandar nasional. Dengan memanfaatkan kemajuan ilmu pengetahuan, teknologi, dan kreativitas, Satala Dermatech Essential hadir untuk mendorong pertumbuhan brand lokal serta membawa industri kecantikan Indonesia menuju masa depan yang lebih progresif, berdaya saing, dan berkelanjutan.
                </p>
              </div>
              {/* Misi */}
              <div className="bg-[#FCFAF6] border border-[#E6D5B8]/30 p-10 rounded-3xl flex flex-col">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#EFE8D8] flex items-center justify-center text-[#8C6208] mb-6 shadow-sm">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="font-bold tracking-widest text-sm text-[#8C6208] uppercase mb-6 text-center">Misi Kami</h4>
                <ul className="space-y-4 text-[#302621] font-medium text-sm">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
                    <span>Menyediakan solusi pengembangan produk kosmetik dan personal care yang inovatif, efektif, dan sesuai dengan kebutuhan pasar.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
                    <span>Menjamin kualitas, keamanan, dan kehalalan produk melalui kerja sama dengan mitra manufaktur berstandar tinggi.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
                    <span>Mendukung pertumbuhan brand lokal dan independen agar mampu bersaing di industri kecantikan nasional maupun global.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
                    <span>Mengoptimalkan penerapan ilmu pengetahuan, teknologi, dan tren kecantikan terkini untuk menghasilkan produk yang relevan dan berdaya saing.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
                    <span>Membangun kemitraan jangka panjang yang berlandaskan kepercayaan, transparansi, dan hasil yang berkelanjutan.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Nilai Kami */}
        <section className="py-20 bg-[#F9F5EC]" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl px-6">
            <div className="text-center mb-16">
              <h3 className="text-[#8C6208] font-bold tracking-widest text-xs mb-3 uppercase">Nilai-Nilai Kami</h3>
              <h2 className="text-3xl font-bold text-[#302621]">Nilai yang Menjadi Landasan Kami</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Fleksibel", desc: "Mitra maklon yang siap membantu terlepas dari skala brand atau konsep awal Anda.", icon: <ShieldCheck /> },
                { title: "Kreativitas Tanpa Batas", desc: "Mewujudkan produk terbaiknya tanpa dibatasi oleh proses teknis formulasi.", icon: <Zap /> },
                { title: "Profesionalisme", desc: "Didukung oleh tim profesional dan jaringan manufaktur berstandar nasional.", icon: <Users /> },
                { title: "Konsistensi Kualitas", desc: "Setiap proses berjalan dengan standar tinggi untuk hasil akhir yang maksimal.", icon: <Heart /> }
              ].map((val, i) => (
                <div key={i} className="bg-white p-8 rounded-2xl border border-[#E6D5B8]/20 shadow-sm text-center">
                  <div className="text-[#8C6208] mb-5 flex justify-center">{val.icon}</div>
                  <h4 className="font-bold text-[#302621] text-lg mb-3">{val.title}</h4>
                  <p className="text-sm text-[#7A695B] leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Keunggulan */}
        <WhyChooseUsSection 
          bgColor="bg-white"
          title={"Keunggulan yang \n Kami Tawarkan"}
          description="Satala Dermatech selalu memprioritaskan transparansi, keamanan, dan dukungan penuh untuk kesuksesan brand Anda."
          items={[
            { title: "Transparansi Data", desc: "Semua data proses maklon tersedia secara transparan untuk mendukung keputusan bisnis yang cepat.", icon: <CheckCircle2 /> },
            { title: "Aman & Terpercaya", desc: "Kerahasiaan formula bersertifikat dengan sistem proteksi ketat.", icon: <ShieldCheck /> },
            { title: "Platform Terintegrasi", desc: "Portal khusus untuk memantau status produksi, stok, dan tagihan secara online.", icon: <Layout /> },
            { title: "Dukungan Profesional", desc: "Tim support kami siap membantu Anda kapan saja melalui berbagai kanal komunikasi.", icon: <MessageSquare /> }
          ]}
        />

        {/* Modul/Layanan */}
        <section className="py-24 px-6 lg:px-12 bg-[#FCFAF6]" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h3 className="text-[#8C6208] font-bold tracking-widest text-xs mb-3 uppercase">Layanan Unggulan</h3>
            <h2 className="text-3xl font-bold text-[#302621] mb-16">Layanan yang Tersedia di Satala Dermatech</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
              {[
                { title: "Riset & Formulasi", desc: "Formulasi unik yang dirancang khusus untuk memenuhi standar pasar terkini.", icon: <Settings /> },
                { title: "Pengadaan Bahan", desc: "Bahan baku premium dan tersertifikasi dari supplier terpercaya.", icon: <Briefcase /> },
                { title: "Produksi Skala Besar", desc: "Mesin produksi modern dengan kapasitas besar dan hasil yang presisi.", icon: <CheckCircle2 /> },
                { title: "Bibit Kreasi Perfumer", desc: "Koleksi bibit eksklusif yang diracik langsung oleh perfumer profesional kami.", icon: <Droplet /> },
                { title: "Legalitas & BPOM", desc: "Pendampingan penuh dalam pengurusan perizinan HKI dan BPOM.", icon: <ShieldCheck /> },
                { title: "Desain Kemasan", desc: "Dukungan visual dan desain packaging yang premium untuk produk Anda.", icon: <Award /> }
              ].map((modul, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm border border-[#E6D5B8]/30 flex gap-4 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-[#EFE8D8] flex items-center justify-center text-[#8C6208]">
                    {modul.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-[#302621] mb-1">{modul.title}</h4>
                    <p className="text-xs text-[#7A695B] leading-relaxed">{modul.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Perjalanan / Timeline */}
        <section className="py-24 bg-white border-y border-[#E6D5B8]/40" data-aos="fade-up">
          <div className="container mx-auto max-w-5xl px-6 text-center">
            <h3 className="text-[#8C6208] font-bold tracking-widest text-xs mb-3 uppercase">Perjalanan Kami</h3>
            <h2 className="text-3xl font-bold text-[#302621] mb-20">Sejarah Satala Dermatech</h2>

            <div className="relative flex flex-col md:flex-row justify-between items-center md:items-start max-w-4xl mx-auto">
              <div className="hidden md:block absolute top-6 left-0 right-0 h-0.5 bg-[#E6D5B8]/50 z-0"></div>
              
              {[
                { year: "2021", title: "Membangun Brand", desc: "Memulai perjalanan dengan membangun brand secara independen sebelum mendirikan pabrik." },
                { year: "2022", title: "Mendirikan Pabrikan", desc: "Mendirikan fasilitas pabrikan sebagai pondasi awal manufaktur." },
                { year: "2023", title: "Pembangunan Pabrik", desc: "Membangun fasilitas manufaktur canggih dengan standar operasional tinggi." },
                { year: "2025", title: "Ekspansi", desc: "Memperluas kapasitas produksi dan menjangkau lebih banyak brand mitra secara berkelanjutan." },
                { year: "2026", title: "Sistem Digital Terintegrasi", desc: "Peluncuran portal digital untuk kemudahan repeat order dan pantau progres secara real-time." }
              ].map((step, idx) => (
                <div key={idx} className="relative z-10 flex flex-col items-center mb-10 md:mb-0 w-48">
                  <div className="w-12 h-12 rounded-full bg-white border-4 border-[#D4AF37] shadow-sm flex items-center justify-center mb-4">
                    <div className="w-3 h-3 bg-[#D4AF37] rounded-full"></div>
                  </div>
                  <h4 className="font-black text-xl text-[#302621] mb-1">{step.year}</h4>
                  <h5 className="font-bold text-[#8C6208] text-sm mb-2">{step.title}</h5>
                  <p className="text-xs text-[#7A695B] text-center leading-relaxed px-2">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimoni */}
        <section className="py-24 bg-[#FCFAF6]" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl px-6 text-center">
            <h3 className="text-[#8C6208] font-bold tracking-widest text-xs mb-3 uppercase">Apa Kata Mereka?</h3>
            <h2 className="text-3xl font-bold text-[#302621] mb-16">Testimoni Pelanggan Kami</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
              {[
                { text: "Satala membantu kami mengintegrasikan visi brand menjadi produk nyata. Efisiensi manufaktur mereka luar biasa.", name: "CV. Untung Terus Alhamdulillah", role: "Direktur Operasional", company: "Minelux" },
                { text: "Sistemnya modern, transparan, dan tim support-nya sangat responsif. Kami selalu tahu progres produk kami.", name: "PT. Cipta Essential Pacific", role: "Manajer Produk", company: "Kokulu" },
                { text: "Kualitas formulasi yang konsisten dan dukungan BPOM yang cepat membuat kami terus mempercayakan maklon di sini.", name: "", role: "Direktur", company: "Bhirama Essentials" }
              ].map((t, idx) => (
                <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E6D5B8]/30 flex flex-col justify-between">
                  <div>
                    <span className="text-[#D4AF37] text-4xl leading-none font-serif block mb-4">"</span>
                    <p className="text-[#4A3B32] leading-relaxed mb-8 italic">"{t.text}"</p>
                  </div>
                  <div className="border-t border-[#E6D5B8]/30 pt-4 mt-auto">
                    <h5 className="font-bold text-[#302621] text-sm">{t.name}</h5>
                    <p className="text-xs text-[#7A695B]">{t.role}</p>
                    <p className="text-xs font-bold text-[#8C6208] mt-2 uppercase">{t.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-5xl">
            <div className="bg-[#302621] rounded-3xl overflow-hidden relative shadow-2xl">
              <div className="absolute inset-0 opacity-40 mix-blend-overlay">
                <img src="/Gambar Web/Botol berdiri.png" alt="Background" className="w-full h-full object-cover" />
              </div>
              <div className="relative z-10 p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-10">
                <div className="text-left w-full md:w-2/3">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Siap Mengembangkan Bisnis Kosmetik Anda?</h2>
                  <p className="text-white/80 text-lg leading-relaxed mb-0">
                    Bergabunglah bersama puluhan brand yang telah merasakan manfaat manufaktur berkualitas dan transparan.
                  </p>
                </div>
                <div className="shrink-0">
                  <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-full shadow-lg border-0 transition-transform hover:-translate-y-1 w-full md:w-auto">
                    Mulai Sekarang
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
